import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, dateFmt, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, TrendingUp, TrendingDown, ArrowRight, Upload } from "lucide-react";

const CATEGORIES = [
  "Software & SaaS", "Travel", "Meals", "Office Supplies", "Internet & Phone",
  "Equipment", "Marketing", "Education", "Subcontractors", "Bank Fees", "Rent", "Other",
];

// Distinct color per category — for visual breakdown bars
const CATEGORY_COLORS = {
  "Software & SaaS": "#d95d39",
  "Travel": "#3d5a8a",
  "Meals": "#d4b872",
  "Office Supplies": "#5a8a6b",
  "Internet & Phone": "#8c6d2a",
  "Equipment": "#6b4aa0",
  "Marketing": "#b83a26",
  "Education": "#4a6b53",
  "Subcontractors": "#0a0a0a",
  "Bank Fees": "#a3a39b",
  "Rent": "#2a446e",
  "Other": "#c5c5b8",
};

const today = () => new Date().toISOString().slice(0, 10);

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: today(), vendor: "", category: "Other", amount: "",
    gst_amount: "", is_input_credit: false, notes: "",
  });
  const [autoCat, setAutoCat] = useState(false);

  const fetchItems = async () => {
    const r = await api.get("/expenses");
    setItems(r.data || []);
    fetchAnalytics();
  };
  const fetchAnalytics = async () => {
    try {
      const r = await api.get("/expenses/analytics");
      setAnalytics(r.data || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchItems(); }, []);

  const aiCategorize = async () => {
    if (!form.vendor) { toast.error("Enter vendor first"); return; }
    setAutoCat(true);
    try {
      const r = await api.post("/ai/categorize-expense", {
        vendor: form.vendor, description: form.notes,
      });
      const cat = r.data?.category;
      if (cat && CATEGORIES.includes(cat)) {
        setForm((f) => ({ ...f, category: cat }));
        toast.success(`Categorized: ${cat}`);
      } else {
        toast.message("Couldn't auto-categorize, picked Other");
      }
    } catch (e) {
      toast.error("AI failed");
    } finally { setAutoCat(false); }
  };

  const submit = async () => {
    if (!form.vendor || !form.amount) { toast.error("Vendor + amount required"); return; }
    try {
      await api.post("/expenses", {
        ...form,
        amount: Number(form.amount),
        gst_amount: Number(form.gst_amount || 0),
      });
      toast.success("Expense logged");
      setOpen(false);
      setForm({ date: today(), vendor: "", category: "Other", amount: "", gst_amount: "", is_input_credit: false, notes: "" });
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/expenses/${id}`);
    toast.success("Removed");
    fetchItems();
  };

  const totalAmt = items.reduce((s, i) => s + (i.amount || 0), 0);
  const totalCredit = items.filter((i) => i.is_input_credit).reduce((s, i) => s + (i.gst_amount || 0), 0);
  const [sourceFilter, setSourceFilter] = useState("all"); // 'all' | 'manual' | 'bank' | 'gmail'

  const filteredItems = items.filter((e) => {
    if (sourceFilter === "all") return true;
    const sk = e.source_kind || "manual";
    if (sourceFilter === "bank") return sk === "bank_csv";
    if (sourceFilter === "gmail") return sk === "gmail_auto";
    if (sourceFilter === "manual") return !sk || sk === "manual";
    return true;
  });
  const bankCount = items.filter((e) => e.source_kind === "bank_csv").length;

  return (
    <div className="space-y-8" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="tc-overline mb-2">Where it goes</p>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Expenses</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/app/import" data-testid="expenses-import-bank-btn">
            <Button variant="outline" className="border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white rounded-md">
              <Upload size={16} className="mr-1.5" /> Import bank statement
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="expense-new-btn" className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
              <Plus size={16} className="mr-1" /> Log expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-2xl">Log an expense</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="expense-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="expense-vendor" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Amount (incl. GST)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="expense-amount" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">GST component</Label>
                <Input type="number" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })}
                  className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid="expense-gst" />
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Category</Label>
                  <button type="button" onClick={aiCategorize} disabled={autoCat}
                    data-testid="expense-ai-categorize"
                    className="flex items-center gap-1 text-xs text-[#d95d39] hover:underline">
                    <Sparkles size={12} /> {autoCat ? "Thinking..." : "AI categorize"}
                  </button>
                </div>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select" className="h-11 rounded-md border-[#e5e5e0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center justify-between bg-[#f4f1eb] rounded-md px-4 py-3">
                <div>
                  <div className="text-sm font-medium">Eligible for input tax credit (ITC)</div>
                  <div className="text-xs text-[#5a5a53]">Vendor is GST-registered &amp; expense is for business</div>
                </div>
                <Switch
                  checked={form.is_input_credit}
                  onCheckedChange={(v) => setForm({ ...form, is_input_credit: v })}
                  data-testid="expense-itc-switch"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-2 h-11 rounded-md border-[#e5e5e0]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="expense-submit" onClick={submit} className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
                Log expense
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
        <div className="bg-white p-6">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53]">Total spent</p>
          <p className="font-['Outfit'] text-3xl font-bold tracking-tight mt-2">{inr(totalAmt)}</p>
        </div>
        <div className="bg-white p-6">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53]">Input credit available</p>
          <p className="font-['Outfit'] text-3xl font-bold tracking-tight mt-2 text-[#4a6b53]">{inr(totalCredit)}</p>
        </div>
      </div>

      <SpendInsights analytics={analytics} />

      {/* Source filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="expenses-source-filter">
        {[
          { id: "all", label: `All (${items.length})` },
          { id: "manual", label: "Manual" },
          { id: "bank", label: `From bank (${bankCount})` },
          { id: "gmail", label: "From Gmail" },
        ].map((f) => (
          <button key={f.id} onClick={() => setSourceFilter(f.id)}
            data-testid={`expenses-source-${f.id}`}
            className={`px-3 py-1.5 text-xs rounded-md border ${sourceFilter === f.id ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "border-[#e5e5e0] text-[#5a5a53] hover:bg-white"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="tc-card overflow-hidden overflow-x-auto">
        {filteredItems.length === 0 ? (
          <div className="p-16 text-center text-sm text-[#5a5a53]" data-testid="expenses-empty">
            {items.length === 0
              ? "No expenses yet. Log a coffee, a SaaS, a flight — your accountant will thank you."
              : "No expenses match this filter."}
          </div>
        ) : (
          <table className="w-full text-sm min-w-[760px]" data-testid="expenses-table">
            <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
              <tr>
                <th className="text-left px-6 py-3 font-bold">Date</th>
                <th className="text-left px-6 py-3 font-bold">Vendor</th>
                <th className="text-left px-6 py-3 font-bold">Category</th>
                <th className="text-left px-6 py-3 font-bold">Source</th>
                <th className="text-right px-6 py-3 font-bold">Amount</th>
                <th className="text-right px-6 py-3 font-bold">GST</th>
                <th className="text-left px-6 py-3 font-bold">ITC</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((e) => (
                <tr key={e.id} className="border-t border-[#e5e5e0] hover:bg-[#fafaf8]">
                  <td className="px-6 py-3 text-[#5a5a53]">{dateFmt(e.date)}</td>
                  <td className="px-6 py-3 font-medium">{e.vendor}</td>
                  <td className="px-6 py-3"><span className="px-2 py-0.5 bg-[#f4f1eb] rounded-sm text-xs">{e.category}</span></td>
                  <td className="px-6 py-3"><SourceBadge sk={e.source_kind} /></td>
                  <td className="px-6 py-3 text-right font-mono">{inr(e.amount)}</td>
                  <td className="px-6 py-3 text-right font-mono text-[#5a5a53]">{inr(e.gst_amount)}</td>
                  <td className="px-6 py-3">{e.is_input_credit ? <span className="text-[#4a6b53] text-xs font-bold">YES</span> : <span className="text-[#a3a39b] text-xs">—</span>}</td>
                  <td className="px-6 py-3 text-right">
                    <button data-testid={`expense-delete-${e.id}`} onClick={() => remove(e.id)}
                      className="text-[#5a5a53] hover:text-[#b83a26]">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SourceBadge({ sk }) {
  if (!sk || sk === "manual") return <span className="text-[10px] text-[#a3a39b]">Manual</span>;
  if (sk === "bank_csv") return <span className="px-1.5 py-0.5 rounded-sm bg-[#fff4ec] text-[#d95d39] text-[10px] font-bold uppercase tracking-wider">Bank</span>;
  if (sk === "gmail_auto") return <span className="px-1.5 py-0.5 rounded-sm bg-[#eef2fa] text-[#3d5a8a] text-[10px] font-bold uppercase tracking-wider">Gmail</span>;
  return <span className="text-[10px] text-[#a3a39b]">{sk}</span>;
}

function SpendInsights({ analytics }) {
  if (!analytics) return null;
  const { this_month_total, last_month_total, fy_total, fy_label, month_delta_pct, by_category, top_vendors, monthly_trend } = analytics;

  // If user has zero expenses, show a friendly "start tracking" empty state instead of dead charts
  if (!fy_total || fy_total === 0) {
    return (
      <div className="tc-card p-6 md:p-8" data-testid="spend-insights-empty">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-[#d95d39] text-white flex items-center justify-center shrink-0">
            <TrendingDown size={18} />
          </div>
          <div className="flex-1">
            <p className="tc-overline" style={{ color: "#d95d39" }}>Where your money goes</p>
            <h2 className="font-['Outfit'] text-xl md:text-2xl font-medium tracking-tight mt-1">
              No spend tracked yet — let's fix that.
            </h2>
            <p className="text-sm text-[#5a5a53] mt-2 max-w-xl">
              Log a few expenses or import your bank statement to see your spending heatmap by category, top vendors, and a 6-month trend.
            </p>
            <Link to="/app/import" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#d95d39] hover:underline">
              Import bank statement <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const maxCat = by_category[0]?.amount || 1;
  const maxMonth = Math.max(...monthly_trend.map((m) => m.amount), 1);
  const trendingUp = month_delta_pct != null && month_delta_pct > 0;

  return (
    <div className="space-y-6" data-testid="spend-insights">
      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
        <div className="bg-white p-4 md:p-6 min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53]">This month</p>
          <p className="font-['Outfit'] text-xl md:text-3xl font-bold mt-2 font-mono break-words">{inr(this_month_total)}</p>
          {month_delta_pct != null && (
            <div className={`mt-2 flex items-center gap-1 text-[11px] font-bold ${trendingUp ? "text-[#b83a26]" : "text-[#4a6b53]"}`}>
              {trendingUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              <span>{trendingUp ? "+" : ""}{month_delta_pct}% vs last month</span>
            </div>
          )}
        </div>
        <div className="bg-white p-4 md:p-6 min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53]">Last month</p>
          <p className="font-['Outfit'] text-xl md:text-3xl font-bold mt-2 font-mono break-words text-[#5a5a53]">{inr(last_month_total)}</p>
        </div>
        <div className="bg-[#0a0a0a] text-white p-4 md:p-6 min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#d95d39]">{fy_label} so far</p>
          <p className="font-['Outfit'] text-xl md:text-3xl font-bold mt-2 font-mono break-words">{inr(fy_total)}</p>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="tc-card p-5 md:p-6" data-testid="spend-by-category">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="tc-overline">Where it went · {fy_label}</p>
            <p className="text-xs text-[#a3a39b] mt-1">By category, biggest at top</p>
          </div>
        </div>
        <div className="space-y-2.5">
          {by_category.slice(0, 12).map((c) => {
            const pct = (c.amount / fy_total) * 100;
            const barPct = (c.amount / maxCat) * 100;
            const color = CATEGORY_COLORS[c.category] || "#a3a39b";
            return (
              <div key={c.category} data-testid={`cat-row-${c.category.replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between text-xs mb-1 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
                    <span className="truncate font-medium text-[#0a0a0a]">{c.category}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[#5a5a53] font-mono text-[11px]">{pct.toFixed(0)}%</span>
                    <span className="font-mono font-bold text-[#0a0a0a]">{inr(c.amount)}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-[#f4f1eb] rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm transition-all" style={{ width: `${barPct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top vendors */}
        <div className="tc-card p-5 md:p-6" data-testid="spend-top-vendors">
          <p className="tc-overline mb-3">Top vendors · {fy_label}</p>
          {top_vendors.length === 0 ? (
            <p className="text-sm text-[#a3a39b]">No vendors yet.</p>
          ) : (
            <div className="space-y-2">
              {top_vendors.map((v) => (
                <div key={v.vendor} className="flex items-center justify-between gap-3 py-2 border-b border-[#f4f1eb] last:border-b-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0a0a0a] truncate" title={v.vendor}>{v.vendor}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#a3a39b]">
                      {v.category} · {v.count} txn{v.count > 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="font-mono font-bold text-sm text-[#0a0a0a] shrink-0">{inr(v.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly trend */}
        <div className="tc-card p-5 md:p-6" data-testid="spend-monthly-trend">
          <p className="tc-overline mb-3">Last 6 months</p>
          <div className="flex items-end gap-2 h-32">
            {monthly_trend.map((m) => {
              const h = (m.amount / maxMonth) * 100;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div className="w-full flex items-end justify-center flex-1">
                    <div
                      className="w-full bg-[#d95d39] rounded-t transition-all hover:bg-[#c84c35]"
                      style={{ height: `${Math.max(h, 2)}%` }}
                      title={`${m.month} · ${inr(m.amount)}`}
                    />
                  </div>
                  <div className="text-[9px] uppercase tracking-wider font-bold text-[#a3a39b]">
                    {m.month.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[11px] text-[#a3a39b] font-mono">
            Peak: {inr(maxMonth)}
          </div>
        </div>
      </div>
    </div>
  );
}

